import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useLocation } from 'react-router-dom';

type AnimationType = 'fade' | 'slideRight' | 'slideLeft' | 'slideUp' | 'slideDown' | 'scale' | 'blur';

interface PageAnimationWrapperProps {
    children: React.ReactNode;
    animationType?: AnimationType;
    duration?: number;
}

export const PageAnimationWrapper: React.FC<PageAnimationWrapperProps> = ({
    children,
    animationType = 'slideRight',
    duration = 500
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setIsVisible(false);

        // Double RAF for smoother animation
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setIsVisible(true);
            });
        });
    }, [location.pathname]);

    // Animation configurations with combined effects
    const animations = {
        fade: {
            visible: {
                opacity: 1,
                transform: 'translateY(0)'
            },
            hidden: {
                opacity: 0,
                transform: `translateY(10px)`
            }
        },
        slideRight: {
            visible: {
                opacity: 1,
                transform: 'translateX(0)'
            },
            hidden: {
                opacity: 0.2,
                transform: 'translateX(-30px)' // Slide from left
            }
        },
        slideLeft: {
            visible: {
                opacity: 1,
                transform: 'translateX(0)'
            },
            hidden: {
                opacity: 0.2,
                transform: 'translateX(30px)' // Slide from right
            }
        },
        slideUp: {
            visible: {
                opacity: 1,
                transform: 'translateY(0)'
            },
            hidden: {
                opacity: 0.2,
                transform: 'translateY(30px)' // Slide from bottom
            }
        },
        slideDown: {
            visible: {
                opacity: 1,
                transform: 'translateY(0)'
            },
            hidden: {
                opacity: 0,
                transform: 'translateY(-30px)' // Slide from top
            }
        },
        scale: {
            visible: {
                opacity: 1,
                transform: 'scale(1)'
            },
            hidden: {
                opacity: 0.2,
                transform: 'scale(0.92)' // Slight zoom
            }
        },
        blur: {
            visible: {
                opacity: 1,
                filter: 'blur(0px)',
                transform: 'translateY(0)'
            },
            hidden: {
                opacity: 0.2,
                filter: 'blur(10px)',
                transform: 'translateY(20px)'
            }
        }
    };

    const currentAnimation = animations[animationType];
    const style = isVisible ? currentAnimation.visible : currentAnimation.hidden;

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                ...style,
                transition: `all ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`,
                animation: 'ease-out',
                willChange: 'opacity, transform, filter'
            }}
        >
            {children}
        </Box>
    );
};
